import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Edit3, Trash2, Mail, Check, AlertTriangle, Eye, BellRing } from 'lucide-react';
import api from './api/axios';
import { formatApiError } from './api/errors';
import OperatingThresholdsTable, { formatCurrency, parseAmount, isValidAmount } from './components/OperatingThresholdsTable';

const MAX_CC_RECIPIENTS = 20;
const CC_EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const CC_ALLOWED_DOMAIN = 'cartiga.com';
const HIDDEN_STAFF_EMAIL = 'dataproj-admin@cartiga.com';
// Splits pasted/typed input on commas, semicolons and whitespace; lowercases and de-dupes.
const mergeCcEntries = (raw, current) => {
  const entries = raw.split(/[,;\s]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
  const next = [...current];
  for (const entry of entries) {
    if ([...entry].some(ch => ch.charCodeAt(0) > 127)) return { error: `"${entry}" has non-English characters. CC addresses must be plain ASCII.` };
    if (/[<>"]/.test(entry)) return { error: 'Enter bare addresses only, such as name@cartiga.com, without names or angle brackets.' };
    if (!CC_EMAIL_PATTERN.test(entry)) return { error: `"${entry}" is not a valid email address` };
    if (entry.slice(entry.lastIndexOf('@') + 1) !== CC_ALLOWED_DOMAIN) {
      return { error: `"${entry}" cannot be CC'd. Only @${CC_ALLOWED_DOMAIN} addresses are allowed.` };
    }
    if (next.includes(entry)) continue;
    if (next.length >= MAX_CC_RECIPIENTS) return { error: `At most ${MAX_CC_RECIPIENTS} CC recipients` };
    next.push(entry);
  }
  return { list: next };
};

// A result without alert_type is an inflow result.
const isLowBalanceResult = (result) => result.alert_type === 'operating_low_balance';
const alertTypeLabel = (alertType) => (alertType === 'operating_low_balance' ? 'Operating low balance' : 'Inflow');
const sentCountLabel = (result) => {
  const [count, noun] = isLowBalanceResult(result)
    ? [result.accounts_count, 'account']
    : [result.firms_count, 'firm'];
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
};

const SubscriptionModal = ({ isOpen, onClose, onSubscriptionChange }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [sendingTestEmails, setSendingTestEmails] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testDays, setTestDays] = useState(7);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    iolta_threshold: 20000,
    checking_threshold: null,
    law_firms: null // null = all firms, [] = specific firms
  });
  const [monitorIolta, setMonitorIolta] = useState(true);
  const [monitorChecking, setMonitorChecking] = useState(false);
  const [notifyOnRemoval, setNotifyOnRemoval] = useState(false);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [notifyOnLowBalance, setNotifyOnLowBalance] = useState(false);
  // String so a blank input is a validation error rather than a silent 0.
  const [lowBalanceDefault, setLowBalanceDefault] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState(null);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoadFailed, setStaffLoadFailed] = useState(false);

  // A type="number" input reports "" for text it cannot parse (e.g. "5,000" or "1e"), which
  // is indistinguishable from blank, so track validity.badInput for the default threshold.
  const [defaultBadInput, setDefaultBadInput] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptions();
      fetchLawFirms();
      fetchStaffUsers();
      // Clear previous test results when modal opens
      setTestEmailResult(null);
      setError(null);
    }
  }, [isOpen]);

  // The error banner sits at the top of a long scrolling form.
  useEffect(() => {
    if (error && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/subscriptions/');
      setSubscriptions(response.data);
    } catch (err) {
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchLawFirms = async () => {
    try {
      const response = await api.get('/api/subscriptions/law-firms');
      setLawFirms(response.data.law_firms);
    } catch (err) {
      console.error('Failed to load law firms:', err);
    }
  };

  const fetchStaffUsers = async () => {
    try {
      const response = await api.get('/api/user/all', { params: { limit: 1000 } });
      setStaffUsers(
        (response.data || []).filter(u => u.is_active && u.email && u.email.toLowerCase() !== HIDDEN_STAFF_EMAIL)
      );
      setStaffLoadFailed(false);
    } catch (err) {
      console.error('Failed to load staff users:', err);
      setStaffLoadFailed(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      iolta_threshold: 20000,
      checking_threshold: null,
      law_firms: null
    });
    setSelectedFirms([]);
    setMonitorIolta(true);
    setMonitorChecking(false);
    setNotifyOnRemoval(false);
    setNotifyOnLowBalance(false);
    setLowBalanceDefault('');
    setCcEmails([]);
    setCcInput('');
    setCcError(null);
    setDefaultBadInput(false);
    setEditingSubscription(null);
    setShowForm(false);
    setTestEmailResult(null);
    setError(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      name: subscription.name,
      iolta_threshold: subscription.iolta_threshold,
      checking_threshold: subscription.checking_threshold,
      law_firms: subscription.law_firms
    });
    setMonitorIolta(subscription.iolta_threshold !== null);
    setMonitorChecking(subscription.checking_threshold !== null);
    setNotifyOnRemoval(subscription.notify_on_removal === true);
    setSelectedFirms(subscription.law_firms || []);
    setNotifyOnLowBalance(subscription.notify_on_low_balance === true);
    setLowBalanceDefault(
      subscription.low_balance_default_threshold == null ? '' : String(subscription.low_balance_default_threshold)
    );
    setCcEmails(subscription.cc_emails || []);
    setCcInput('');
    setCcError(null);
    setDefaultBadInput(false);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Subscription name is required');
      return;
    }

    if (!monitorIolta && !monitorChecking && !notifyOnRemoval && !notifyOnLowBalance) {
      setError('Select at least one alert: IOLTA or checking inflow, operating low-balance, or account removal');
      return;
    }

    // The server stores an empty firm list as "all firms", so require a real selection.
    if (formData.law_firms !== null && selectedFirms.length === 0) {
      setError('Select at least one law firm, or choose All Law Firms');
      return;
    }

    const lowBalanceDefaultValue = parseAmount(lowBalanceDefault);
    if (notifyOnLowBalance && (defaultBadInput || lowBalanceDefaultValue === null || !isValidAmount(lowBalanceDefaultValue))) {
      setError('Enter a default low-balance threshold of 0 or more');
      return;
    }

    // Commit an address that was typed but not yet added.
    let finalCcEmails = ccEmails;
    if (ccInput.trim()) {
      const merged = mergeCcEntries(ccInput, ccEmails);
      if (merged.error) {
        setCcError(merged.error);
        setError(merged.error);
        return;
      }
      finalCcEmails = merged.list;
      setCcEmails(finalCcEmails);
      setCcInput('');
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        name: formData.name,
        iolta_threshold: monitorIolta ? (formData.iolta_threshold || 0) : null,
        checking_threshold: monitorChecking ? (formData.checking_threshold || 0) : null,
        notify_on_removal: notifyOnRemoval,
        notify_on_low_balance: notifyOnLowBalance,
        low_balance_default_threshold: notifyOnLowBalance ? lowBalanceDefaultValue : null,
        cc_emails: finalCcEmails,
        law_firms: formData.law_firms === null ? null : selectedFirms
      };

      if (editingSubscription) {
        await api.put(`/api/subscriptions/${editingSubscription.id}`, submitData);
      } else {
        await api.post('/api/subscriptions/', submitData);
      }

      await fetchSubscriptions();
      resetForm();
      if (onSubscriptionChange) onSubscriptionChange();
    } catch (err) {
      setError(formatApiError(err, 'Failed to save subscription'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/subscriptions/${subscriptionId}`);
      await fetchSubscriptions();
      if (onSubscriptionChange) onSubscriptionChange();
    } catch (err) {
      setError('Failed to delete subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (subscription) => {
    try {
      setLoading(true);
      await api.put(`/api/subscriptions/${subscription.id}`, {
        is_active: !subscription.is_active
      });
      await fetchSubscriptions();
      if (onSubscriptionChange) onSubscriptionChange();
    } catch (err) {
      setError('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleFirmToggle = (firmName) => {
    const newSelected = selectedFirms.includes(firmName)
      ? selectedFirms.filter(f => f !== firmName)
      : [...selectedFirms, firmName];
    setSelectedFirms(newSelected);
  };

  const handleTestEmails = async () => {
    try {
      setSendingTestEmails(true);
      setError(null);
      setTestEmailResult(null);

      const response = await api.post(`/api/subscriptions/test-send-emails?days=${testDays}`);
      setTestEmailResult(response.data);
      
      if (response.data.success) {
        setError(null);
      } else {
        setError(response.data.error || 'Failed to send test emails');
      }
    } catch (err) {
      setError(formatApiError(err, 'Failed to send test emails'));
      setTestEmailResult(null);
    } finally {
      setSendingTestEmails(false);
    }
  };

  const handlePreview = async (subscriptionId) => {
    try {
      const response = await api.post(`/api/subscriptions/${subscriptionId}/preview?days=${testDays}`);
      const previewWindow = window.open('', '_blank');
      previewWindow.document.write(response.data);
      previewWindow.document.close();
    } catch (err) {
      setError(formatApiError(err, 'Failed to generate preview'));
    }
  };

  const handlePreviewLowBalance = async (subscriptionId) => {
    try {
      const response = await api.post(`/api/subscriptions/${subscriptionId}/preview-low-balance`);
      const previewWindow = window.open('', '_blank');
      if (!previewWindow) {
        setError('The preview was blocked by the browser. Allow pop-ups for this site and try again.');
        return;
      }
      previewWindow.document.write(response.data);
      previewWindow.document.close();
    } catch (err) {
      setError(formatApiError(err, 'Failed to generate low-balance preview'));
    }
  };

  const addCcFromInput = () => {
    if (!ccInput.trim()) return;
    const merged = mergeCcEntries(ccInput, ccEmails);
    if (merged.error) {
      setCcError(merged.error);
      return;
    }
    setCcEmails(merged.list);
    setCcInput('');
    setCcError(null);
  };

  const addStaffCc = (email) => {
    if (!email) return;
    const merged = mergeCcEntries(email, ccEmails);
    if (merged.error) {
      setCcError(merged.error);
      return;
    }
    setCcEmails(merged.list);
    setCcError(null);
  };

  const removeCc = (email) => {
    setCcEmails(ccEmails.filter(e => e !== email));
    setCcError(null);
  };

  if (!isOpen) return null;

  const allFirmsSelected = formData.law_firms === null;
  const defaultAmount = parseAmount(lowBalanceDefault);
  const defaultThreshold = !defaultBadInput && defaultAmount !== null && isValidAmount(defaultAmount) ? defaultAmount : null;
  const staffByEmail = new Map(staffUsers.map(u => [u.email.toLowerCase(), u]));
  const staffChoices = staffUsers
    .filter(u => !ccEmails.includes(u.email.toLowerCase()))
    .sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="text-white px-6 py-4 flex justify-between items-center bg-brand-600">
          <div className="flex items-center space-x-2">
            <Mail className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Email Alert Subscriptions</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}

          {testEmailResult && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md mb-4">
              <h4 className="font-medium mb-2">Email Send Results</h4>
              <div>
                <p>Emails sent: {testEmailResult.emails_sent} / {testEmailResult.total_subscriptions} subscriptions</p>
                {testEmailResult.results && testEmailResult.results.length > 0 && (
                  <div className="mt-2 text-sm">
                    {testEmailResult.results.map((result, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{result.user_email} ({alertTypeLabel(result.alert_type)}): </span>
                        <span className={result.status === 'sent' ? 'text-green-600' :
                                       result.status === 'no_alerts' ? 'text-gray-600' : 'text-red-600'}>
                          {result.status === 'sent' ? `✓ Sent (${sentCountLabel(result)})` :
                           result.status === 'no_alerts' ? `- ${result.message || 'No alerts'}` :
                           `✗ ${result.message || 'Failed'}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {!showForm ? (
            <div>
              {/* Action buttons */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Your Subscriptions</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleTestEmails}
                    disabled={sendingTestEmails}
                    className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 disabled:opacity-50 transition-all duration-200"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingTestEmails ? 'Sending...' : 'Send Emails Now'}
                  </button>
                  <select
                    value={testDays}
                    onChange={(e) => setTestDays(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm focus:outline-none"
                    title="Date range for preview/test"
                  >
                    <option value={1}>Last 1 day</option>
                    <option value={3}>Last 3 days</option>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                  </select>
                  <button
                    onClick={handleCreateNew}
                    className="flex items-center px-4 py-2 text-white rounded-full bg-brand-600 hover:bg-brand-700 transition-all duration-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Subscription
                  </button>
                </div>
              </div>

              {/* Subscriptions list */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-b-brand-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading subscriptions...</p>
                </div>
              ) : subscriptions.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Subscriptions</h3>
                  <p className="text-gray-600 mb-4">Create your first email alert subscription to get notified of significant balance changes.</p>
                  <button
                    onClick={handleCreateNew}
                    className="inline-flex items-center px-4 py-2 text-white rounded-full bg-brand-600 hover:bg-brand-700 transition-all duration-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Subscription
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {subscriptions.map((subscription) => (
                    <div key={subscription.id} className="border border-gray-200 rounded-3xl p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-medium text-gray-900">{subscription.name}</h4>
                            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              subscription.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {subscription.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {subscription.notify_on_removal && (
                              <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                Removal Alerts
                              </span>
                            )}
                            {subscription.notify_on_low_balance && (
                              <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-800">
                                Low-balance, default {formatCurrency(subscription.low_balance_default_threshold)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><strong>Inflow thresholds:</strong> {[
                              subscription.iolta_threshold !== null ? `IOLTA: ${formatCurrency(subscription.iolta_threshold)}` : null,
                              subscription.checking_threshold !== null ? `Checking: ${formatCurrency(subscription.checking_threshold)}` : null
                            ].filter(Boolean).join(' | ') || 'None'}</p>
                            <p><strong>Law Firms:</strong> {
                              !subscription.law_firms 
                                ? 'All Law Firms' 
                                : subscription.law_firms.length === 0
                                  ? 'None Selected'
                                  : subscription.law_firms.join(', ')
                            }</p>
                            {subscription.cc_emails && subscription.cc_emails.length > 0 && (
                              <p title={subscription.cc_emails.join(', ')}>
                                <strong>CC:</strong> {subscription.cc_emails.length} {subscription.cc_emails.length === 1 ? 'recipient' : 'recipients'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {(subscription.iolta_threshold != null || subscription.checking_threshold != null) && (
                            <button
                              onClick={() => handlePreview(subscription.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-all duration-200"
                              title="Preview inflow email"
                              aria-label="Preview inflow email"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {subscription.notify_on_low_balance && (
                            <button
                              onClick={() => handlePreviewLowBalance(subscription.id)}
                              className="p-2 text-rose-600 hover:bg-rose-50 rounded-full transition-all duration-200"
                              title="Preview low-balance email"
                              aria-label="Preview low-balance email"
                            >
                              <BellRing className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleActive(subscription)}
                            className={`p-2 rounded-full transition-all duration-200 ${
                              subscription.is_active
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                            title={subscription.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(subscription)}
                            className="p-2 rounded-full text-brand-600 hover:bg-brand-50 transition-all duration-200"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(subscription.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-all duration-200"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Subscription Form */
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSubscription ? 'Edit Subscription' : 'New Subscription'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., High Value Alerts"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
                    required
                  />
                </div>

                {/* Account Type Monitoring */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Alerts to Receive *
                  </label>

                  {/* IOLTA Section */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={monitorIolta}
                        onChange={(e) => setMonitorIolta(e.target.checked)}
                        className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                      />
                      <span className="text-sm font-medium text-gray-700">IOLTA inflow alerts</span>
                    </label>
                    {monitorIolta && (
                      <div className="mt-3 ml-6">
                        <label className="block text-xs text-gray-500 mb-1">IOLTA inflow threshold</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={formData.iolta_threshold ?? ''}
                            onChange={(e) => setFormData({...formData, iolta_threshold: parseFloat(e.target.value) || 0})}
                            min="0"
                            step="1000"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Checking Section */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={monitorChecking}
                        onChange={(e) => setMonitorChecking(e.target.checked)}
                        className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Checking inflow alerts</span>
                    </label>
                    {monitorChecking && (
                      <div className="mt-3 ml-6">
                        <label className="block text-xs text-gray-500 mb-1">Checking inflow threshold</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={formData.checking_threshold ?? ''}
                            onChange={(e) => setFormData({...formData, checking_threshold: parseFloat(e.target.value) || 0})}
                            min="0"
                            step="1000"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Account Removal Alerts */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={notifyOnRemoval}
                        onChange={(e) => setNotifyOnRemoval(e.target.checked)}
                        className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Notify me when an account is removed from Plaid</span>
                    </label>
                    <p className="mt-2 ml-6 text-xs text-gray-500">
                      Sends one consolidated email per sync run listing every account that stopped appearing in Plaid, across all matching law firms.
                    </p>
                  </div>

                  {/* Operating account low-balance alerts */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={notifyOnLowBalance}
                        onChange={(e) => setNotifyOnLowBalance(e.target.checked)}
                        className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Operating account low-balance alerts</span>
                    </label>
                    <p className="mt-2 ml-6 text-xs text-gray-500">
                      A daily email at 8:00 AM Eastern listing each operating account whose balance is below its threshold: the account's own override if one is set, otherwise the default below.
                    </p>
                    {notifyOnLowBalance && (
                      <div className="mt-3 ml-6">
                        <label htmlFor="low-balance-default" className="block text-xs text-gray-500 mb-1">Default low-balance threshold *</label>
                        <div className="relative max-w-xs">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            id="low-balance-default"
                            type="number"
                            value={lowBalanceDefault}
                            onChange={(e) => {
                              setLowBalanceDefault(e.target.value);
                              setDefaultBadInput(e.target.validity.badInput);
                            }}
                            min="0"
                            step="0.01"
                            placeholder="e.g. 5000"
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">
                    Select at least one alert: IOLTA or checking inflow, operating low-balance, or account removal. Unchecked inflow account types are not monitored.
                  </p>
                </div>

                {/* Law Firms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Law Firms to Monitor
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="firmSelection"
                        checked={formData.law_firms === null}
                        onChange={() => setFormData({...formData, law_firms: null})}
                        className="h-4 w-4 accent-brand-600"
                      />
                      <span className="text-sm text-gray-700">All Law Firms</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="firmSelection"
                        checked={formData.law_firms !== null}
                        onChange={() => setFormData({...formData, law_firms: selectedFirms})}
                        className="h-4 w-4 accent-brand-600"
                      />
                      <span className="text-sm text-gray-700">Specific Law Firms</span>
                    </label>
                  </div>

                  {formData.law_firms !== null && selectedFirms.length === 0 && (
                    <p className="mt-2 text-xs text-red-700">Select at least one law firm.</p>
                  )}

                  {formData.law_firms !== null && (
                    <div className="mt-3 border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {lawFirms.map((firm) => (
                          <label key={firm} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={selectedFirms.includes(firm)}
                              onChange={() => handleFirmToggle(firm)}
                              className="h-4 w-4 border-gray-300 rounded accent-brand-600"
                            />
                            <span className="text-sm text-gray-700">{firm}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Operating account low-balance table */}
                {notifyOnLowBalance && (
                  <OperatingThresholdsTable
                    allFirmsSelected={allFirmsSelected}
                    selectedFirms={selectedFirms}
                    defaultThreshold={defaultThreshold}
                  />
                )}

                {/* CC recipients */}
                <div>
                  <label htmlFor="cc-input" className="block text-sm font-medium text-gray-700 mb-1">
                    CC Recipients
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Copied on every alert from this subscription: inflow, low-balance and removal. Only @cartiga.com addresses can be CC'd. Your own address is skipped.
                  </p>

                  {ccEmails.length > 0 && (
                    <ul className="flex flex-wrap gap-2 mb-2" aria-label="CC recipients">
                      {ccEmails.map((email) => {
                        const staff = staffByEmail.get(email);
                        return (
                          <li
                            key={email}
                            className="inline-flex items-center pl-3 pr-1 py-1 text-sm bg-gray-100 text-gray-800 rounded-full"
                            title={staff?.full_name ? `${staff.full_name} <${email}>` : email}
                          >
                            <span>{email}</span>
                            <button
                              type="button"
                              onClick={() => removeCc(email)}
                              className="ml-1 p-1 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                              aria-label={`Remove ${email}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value=""
                      onChange={(e) => addStaffCc(e.target.value)}
                      disabled={ccEmails.length >= MAX_CC_RECIPIENTS || staffChoices.length === 0}
                      aria-label="Add a staff member to CC"
                      className="px-3 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 disabled:opacity-50"
                    >
                      <option value="">{staffLoadFailed ? 'Staff list unavailable' : 'Add a staff member'}</option>
                      {staffChoices.map(u => (
                        <option key={u.id} value={u.email}>{u.full_name ? `${u.full_name} (${u.email})` : u.email}</option>
                      ))}
                    </select>
                    <input
                      id="cc-input"
                      type="text"
                      inputMode="email"
                      value={ccInput}
                      onChange={(e) => { setCcInput(e.target.value); setCcError(null); }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          addCcFromInput();
                        }
                      }}
                      disabled={ccEmails.length >= MAX_CC_RECIPIENTS}
                      placeholder="Or type a @cartiga.com address"
                      className="flex-1 min-w-[14rem] px-3 py-2 text-sm border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={addCcFromInput}
                      disabled={!ccInput.trim() || ccEmails.length >= MAX_CC_RECIPIENTS}
                      className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-1 flex justify-between text-xs">
                    <span className="text-red-700" role="alert">{ccError}</span>
                    <span className="text-gray-500">{ccEmails.length} of {MAX_CC_RECIPIENTS}</span>
                  </div>
                </div>

                {/* Submit buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-white rounded-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 transition-all duration-200"
                  >
                    {loading ? 'Saving...' : editingSubscription ? 'Update' : 'Create'} Subscription
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;